import { useEffect, useRef, useState } from "react";
import { Loader2, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { openWhatsApp, buildEnquiryMessage } from "@/lib/whatsapp";
import { useEnquiryPopup } from "@/context/EnquiryPopupContext";

// First appearance: 10s after the site loads.
const AUTO_SHOW_DELAY_MS = 10000;
// Then re-appears every 40s until the visitor submits the enquiry.
const AUTO_SHOW_REPEAT_MS = 40000;
// Stored in localStorage so that once a visitor submits the form,
// the popup stops auto-showing for good (even across tabs/sessions).
const SUBMITTED_FLAG = "iiutcstudy_popup_submitted";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[0-9+\-\s()]{7,16}$/;

const EMPTY_FORM = { name: "", phone: "", email: "", course: "", message: "" };

export default function EnquiryPopup() {
  const { isOpen, prefill, openPopup, closePopup } = useEnquiryPopup();
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  // Prefill fields (e.g. course name) when opened from a specific CTA.
  useEffect(() => {
    if (isOpen && prefill) {
      setForm((f) => ({ ...f, ...prefill }));
    }
  }, [isOpen, prefill]);

  // First appearance 10s after the site loads, then every 40s again,
  // until the visitor submits the enquiry (tracked in localStorage).
  useEffect(() => {
    const hasSubmitted = () => {
      try {
        return localStorage.getItem(SUBMITTED_FLAG) === "1";
      } catch {
        return false;
      }
    };

    if (hasSubmitted()) return undefined;

    const scheduleNext = (delay) => {
      timerRef.current = setTimeout(() => {
        if (hasSubmitted()) return;
        openPopup();
        scheduleNext(AUTO_SHOW_REPEAT_MS);
      }, delay);
    };

    scheduleNext(AUTO_SHOW_DELAY_MS);

    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (key) => (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((er) => (er[key] ? { ...er, [key]: undefined } : er));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name.";
    if (!form.phone.trim()) next.phone = "Please enter your phone number.";
    else if (!PHONE_RE.test(form.phone.trim())) next.phone = "Enter a valid phone number.";
    if (!form.email.trim()) next.email = "Please enter your email address.";
    else if (!EMAIL_RE.test(form.email.trim())) next.email = "Enter a valid email address.";
    if (!form.course.trim()) next.course = "Please tell us which course you're interested in.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleClose = (open) => {
    if (!open) closePopup();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!validate()) {
      toast.error("Please fill in all required fields correctly.");
      return;
    }

    setLoading(true);
    try {
      // Small delay so the loading state is visible before hand-off to WhatsApp.
      await new Promise((resolve) => setTimeout(resolve, 350));
      const message = buildEnquiryMessage(form);
      closePopup();
      try {
        localStorage.setItem(SUBMITTED_FLAG, "1");
      } catch {
        // ignore storage errors (private browsing etc.)
      }
      if (timerRef.current) clearTimeout(timerRef.current);
      toast.success("Thanks! Opening WhatsApp with your details…");
      openWhatsApp(message);
      setForm(EMPTY_FORM);
      setErrors({});
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-lg w-[92vw] rounded-3xl border-0 p-0 bg-white overflow-hidden max-h-[88vh] overflow-y-auto"
        data-testid="enquiry-popup"
      >
        <div className="p-6 md:p-8">
          <DialogHeader className="text-left">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#0A3161]/5 border border-[#0A3161]/10 px-3 py-1 text-[11px] font-semibold text-[#0A3161] w-fit">
              <Sparkles className="h-3.5 w-3.5" /> Free Counseling Session
            </div>
            <DialogTitle className="mt-3 font-display text-2xl font-extrabold text-slate-900">
              Enquiry Form
            </DialogTitle>
          </DialogHeader>
          <p className="mt-1 text-sm text-slate-500">
            Take the first step towards your dream university! Get expert guidance on admissions,
            scholarships, visas, and career opportunities. Apply today and let our counselors help
            you achieve your study abroad goals.
          </p>

          <form
            onSubmit={submit}
            noValidate
            className="mt-5 space-y-4"
            data-testid="enquiry-popup-form"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <PopupField label="Full Name" required error={errors.name}>
                <input
                  value={form.name}
                  onChange={update("name")}
                  data-testid="popup-input-name"
                  placeholder="Your full name"
                  className={inputClass(errors.name)}
                />
              </PopupField>
              <PopupField label="Mobile Number" required error={errors.phone}>
                <input
                  value={form.phone}
                  onChange={update("phone")}
                  data-testid="popup-input-phone"
                  placeholder="+91 98765 43210"
                  className={inputClass(errors.phone)}
                />
              </PopupField>
              <PopupField label="Email Address" required error={errors.email} className="sm:col-span-2">
                <input
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  data-testid="popup-input-email"
                  placeholder="you@example.com"
                  className={inputClass(errors.email)}
                />
              </PopupField>
            </div>

            <PopupField label="Course Interested In" required error={errors.course}>
              <input
                value={form.course}
                onChange={update("course")}
                data-testid="popup-input-course"
                placeholder="e.g. MERN Stack, MS in Computer Science…"
                className={inputClass(errors.course)}
              />
            </PopupField>

            <PopupField label="Message" error={errors.message}>
              <textarea
                value={form.message}
                onChange={update("message")}
                rows={3}
                data-testid="popup-input-message"
                placeholder="Tell us anything else that helps us assist you…"
                className={`${inputClass(errors.message)} resize-none`}
              />
            </PopupField>

            <button
              type="submit"
              disabled={loading}
              data-testid="popup-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#B31942] text-white px-6 py-3.5 text-sm font-semibold hover:bg-[#940835] disabled:opacity-70 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {loading ? "Submitting…" : "Submit Enquiry"}
            </button>
            <p className="text-center text-xs text-slate-400">
              Your info stays private. We'll reach out to you on WhatsApp within minutes.
            </p>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function inputClass(error) {
  return `w-full h-11 rounded-xl border bg-white px-4 text-sm focus:outline-none focus:ring-2 transition-colors ${
    error
      ? "border-red-400 focus:border-red-400 focus:ring-red-400/15"
      : "border-black/10 focus:border-[#0A3161] focus:ring-[#0A3161]/15"
  }`;
}

function PopupField({ label, required, error, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[12px] font-semibold text-slate-700 uppercase tracking-wider">
        {label} {required && <span className="text-[#B31942]">*</span>}
      </span>
      <div className="mt-1.5">{children}</div>
      {error && <span className="mt-1 block text-xs font-medium text-red-500">{error}</span>}
    </label>
  );
}
